import React, { useState, useEffect, useRef } from 'react';
import { fetchTotalItems, getAllFoods, recommendHybrid } from '../utils/api';
import './PreferenceForm.css';
import { ATTRIBUTES, SMELL_ATTRIBUTES, exclusionKeyFor } from '../constants/attributes';
import { computePositionFromValue } from '../utils/number';

// ─── Curated taste food examples (integer level 1-10) ────────────────────────
const FOOD_EXAMPLES = {
  Spice:  { 1:'Plain rice', 2:'Banana', 3:'Pasta', 4:'Mild salsa', 5:'Tacos', 6:'Pad Thai', 7:'Jalapeño', 8:'Spicy curry', 9:'Hot wings', 10:'Ghost pepper sauce' },
  Sour:   { 1:'Banana', 2:'Sweet potato', 3:'Milk', 4:'Yogurt', 5:'Orange', 6:'Pickle', 7:'Sauerkraut', 8:'Lemon', 9:'Lime juice', 10:'Vinegar' },
  Sweet:  { 1:'Egg', 2:'Chicken', 3:'Cheese', 4:'Corn', 5:'Apple', 6:'Milk chocolate', 7:'Ice cream', 8:'Honey', 9:'Chocolate cake', 10:'Gummy candy' },
  Bitter: { 1:'White rice', 2:'Pasta', 3:'Bread', 4:'Chicken', 5:'Spinach', 6:'Dark chocolate', 7:'Coffee', 8:'Grapefruit', 9:'Arugula', 10:'Espresso' },
  Umami:  { 1:'Apple', 2:'White rice', 3:'Potato', 4:'Egg', 5:'Tomato', 6:'Mushroom', 7:'Chicken', 8:'Parmesan', 9:'Soy sauce', 10:'Miso paste' },
  Salt:   { 1:'Plain rice', 2:'Apple', 3:'Pasta', 4:'Egg', 5:'Bread', 6:'French fries', 7:'Chips', 8:'Soy sauce', 9:'Pretzels', 10:'Anchovies' },
  Fatty:  { 1:'Apple', 2:'Chicken breast', 3:'Rice', 4:'Whole milk', 5:'Avocado', 6:'Cheese', 7:'Burger', 8:'Bacon', 9:'Butter', 10:'Deep-fried food' },
};

// ─── Curated smell food examples (integer level 1-10) ────────────────────────
const SMELL_EXAMPLES = {
  Smoky:     { 1:'Steamed veggies', 2:'Boiled chicken', 3:'Roasted tomato', 4:'Grilled zucchini', 5:'Smoked gouda', 6:'Smoked salmon', 7:'Bacon', 8:'BBQ ribs', 9:'Smoked brisket', 10:'Charred BBQ' },
  Herbal:    { 1:'Plain rice', 2:'Pasta', 3:'Roast chicken', 4:'Tomato sauce', 5:'Pesto', 6:'Chimichurri', 7:'Tabbouleh', 8:'Herb salad', 9:'Basil oil', 10:'Fresh herb tea' },
  Citrusy:   { 1:'Milk', 2:'Rice', 3:'Pasta', 4:'Yogurt', 5:'Lemon chicken', 6:'Lemon tart', 7:'Lemon curd', 8:'Orange cake', 9:'Lime sorbet', 10:'Citrus vinaigrette' },
  Fermented: { 1:'Fresh milk', 2:'Bread', 3:'Yogurt', 4:'Sour cream', 5:'Cheddar', 6:'Kimchi', 7:'Sauerkraut', 8:'Miso', 9:'Blue cheese', 10:'Natto' },
  Roasted:   { 1:'Steamed rice', 2:'Boiled potato', 3:'Baked bread', 4:'Toasted bread', 5:'Roasted nuts', 6:'Light roast coffee', 7:'Dark roast coffee', 8:'Roasted garlic', 9:'Espresso', 10:'Charred steak' },
  Fresh:     { 1:'Canned soup', 2:'Cooked beans', 3:'Cooked pasta', 4:'Sliced apple', 5:'Garden salad', 6:'Cucumber', 7:'Mint', 8:'Fresh herbs', 9:'Watermelon', 10:'Chilled mint water' },
  SweetAroma:{ 1:'Plain bread', 2:'Toast', 3:'Butter cookie', 4:'Banana bread', 5:'Vanilla cake', 6:'Cinnamon roll', 7:'Caramel', 8:'Chocolate brownie', 9:'Bakery shop', 10:'Cotton candy' },
  Pungent:   { 1:'Plain rice', 2:'Butter', 3:'Onion rings', 4:'Garlic bread', 5:'Sriracha', 6:'Mustard', 7:'Raw onion', 8:'Blue cheese', 9:'Raw garlic', 10:'Durian' },
  Earthy:    { 1:'White rice', 2:'Chicken broth', 3:'Potato', 4:'Lentils', 5:'Mushroom', 6:'Truffle oil', 7:'Beets', 8:'Black truffle', 9:'Aged mushroom', 10:'Earthy herbal tea' },
};

const tasteExample = (attr, value) => {
  const key = Math.max(1, Math.min(10, Math.round(parseFloat(value))));
  return FOOD_EXAMPLES[attr]?.[key] ?? '';
};
const smellExample = (attr, value) => {
  const key = Math.max(1, Math.min(10, Math.round(parseFloat(value))));
  return SMELL_EXAMPLES[attr]?.[key] ?? '';
};

// Show integer if whole number, else 1 decimal
const displayPref = (v) => {
  const n = parseFloat(v);
  return Number.isInteger(n) ? `${n}` : n.toFixed(1);
};

// ─── Defaults ─────────────────────────────────────────────────────────────────
const DEFAULT_PREFS      = () => ({ Spice:5, Sour:5, Sweet:5, Bitter:5, Umami:5, Salt:5, Fatty:5, hot_cold:'Both' });
const DEFAULT_SMELL_PREFS= () => ({ Smoky:5, Herbal:5, Citrusy:5, Fermented:5, Roasted:5, Fresh:5, SweetAroma:5, Pungent:5, Earthy:5 });
const DEFAULT_TASTE_EXCL = () => ({ excludeSpice:false, excludeSour:false, excludeSweet:false, excludeBitter:false, excludeUmami:false, excludeSalt:false, excludeFatty:false });
const DEFAULT_SMELL_EXCL = () => Object.fromEntries(SMELL_ATTRIBUTES.map(a => [exclusionKeyFor(a), true]));

// ─── Instructions content ─────────────────────────────────────────────────────
function InstructionsPage() {
  return (
    <div className="instructions-page">
      <h2 className="instr-title">How to Use JustTaste</h2>

      <div className="instr-section">
        <h3>Step 1 — Create an account</h3>
        <p>
          Click <strong>Login</strong> in the top-right corner and register a free account.
          Logging in saves your taste profile so that every piece of feedback you give
          makes future recommendations smarter and more personal to you.
          You can still use the sliders and get recommendations without logging in,
          but your preferences won't be remembered between sessions.
        </p>
      </div>

      <div className="instr-section">
        <h3>Step 2 — Using the taste sliders</h3>
        <p>
          Each slider controls one dimension of taste on a scale of <strong>1 to 10</strong>.
          When you <strong>drag the slider</strong> it snaps to whole numbers (1, 2, 3 … 10)
          and shows a typical food at that level so you can calibrate quickly.
          When you click the <strong>▲ / ▼ arrows</strong> the value moves in steps of 0.1,
          letting you fine-tune to in-between positions like 6.4 or 7.8.
          Leaving a slider at 5 (neutral) tells the algorithm that dimension doesn't matter much to you right now.
        </p>
      </div>

      <div className="instr-section">
        <h3>Step 3 — Aroma preferences (optional)</h3>
        <p>
          Click <strong>"▼ Add Aroma Preferences"</strong> to expand the olfactory section.
          By default all aromas are excluded — they don't affect your results unless you turn them on.
          Uncheck <strong>"Exclude All Aromas"</strong> at the top to enable them all,
          or toggle individual aromas on and off with their own checkboxes.
          Aromas work the same as taste sliders: drag for whole numbers, use ▲/▼ for 0.1 steps.
          Research shows smell accounts for up to 27% of perceived flavour, so enabling aromas
          can meaningfully improve how well recommendations match what you're craving.
        </p>
      </div>

      <div className="instr-section">
        <h3>Step 4 — Rate your results</h3>
        <p>
          After you get recommendations, click <strong>"▼ Rate this recommendation"</strong>
          under any result. You don't need to be logged in to leave feedback —
          just rate craving fit, discovery fit, and add an optional comment.
          Every rating trains the algorithm to learn what you actually enjoy versus what just looks good on paper.
        </p>
      </div>

      {/* ── Taste Explanations ───────────────────────────────────────────── */}
      <div className="instr-section taste-glossary">
        <h3>Taste dimensions — what do they mean?</h3>

        <div className="gloss-entry">
          <span className="gloss-label">Spice</span>
          <p>The burning or tingling heat from ingredients like chili peppers or black pepper.
          Level 1 is completely mild; level 10 is intensely hot.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Sour</span>
          <p>The sharp, mouth-puckering taste from acids in foods like citrus fruits, vinegar, and pickles.
          Level 1 is neutral; level 10 is as tangy as biting straight into a lemon.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Sweet</span>
          <p>The pleasant sugary taste from natural or added sugars.
          Level 1 is completely unsweetened; level 10 is as sweet as candy or syrup.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Bitter</span>
          <p>A sharp, slightly dry taste found in coffee, dark chocolate, beer, and leafy greens.
          Level 1 has no bitterness; level 10 is as intense as straight espresso.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Umami</span>
          <p>A deep, savory "meaty" taste from proteins and glutamates found in mushrooms, parmesan, soy sauce, and meat.
          Level 1 is clean and light; level 10 is rich and intensely savory.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Salt</span>
          <p>The well-known savory taste from sodium that also enhances every other flavor.
          Level 1 is essentially unsalted; level 10 is very salty like anchovies or pretzels.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Fatty</span>
          <p>The rich, creamy, or oily mouthfeel from fats in butter, avocado, bacon, and fried foods.
          Level 1 is lean and light; level 10 is very rich and indulgent.</p>
        </div>
      </div>

      {/* ── Aroma Explanations ───────────────────────────────────────────── */}
      <div className="instr-section taste-glossary">
        <h3>Aroma dimensions — what do they mean?</h3>

        <div className="gloss-entry">
          <span className="gloss-label">Smoky</span>
          <p>The deep, charred smell created when food is cooked over fire, smoked, or barbecued.
          Think campfire, bacon, or smoked salmon.
          High smoky means the food smells strongly of wood smoke or char.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Herbal</span>
          <p>The fresh, green scent of herbs like basil, cilantro, mint, or rosemary — the smell of a garden.
          High herbal means the food has a strong, fresh, plant-forward aroma.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Citrusy</span>
          <p>The bright, zesty smell of lemon, lime, or orange peel — even in foods that aren't sour.
          High citrusy means a sharp, clean, refreshing lemon-peel type scent.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Fermented</span>
          <p>The tangy, funky, complex smell from foods aged or cultured by bacteria or yeast — like cheese, kimchi, or sourdough.
          It's not a bad smell; it adds depth.
          High fermented means a strong, aged, pungent aroma like blue cheese or miso.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Roasted</span>
          <p>The warm, nutty, toasty aroma created when heat browns sugars and proteins — think fresh coffee, toasted bread, or seared steak.
          High roasted means a deep, coffee-like or caramelized smell.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Fresh</span>
          <p>A clean, cool, light scent like cucumber, watermelon, or mint — the smell of something just picked.
          High fresh means a crisp, airy aroma with very little processing or heat behind it.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">SweetAroma</span>
          <p>The warm, bakery-like smell of vanilla, caramel, cinnamon, or chocolate — the scent of a pastry shop.
          High sweet aroma means the food smells dessert-like even before you taste it.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Pungent</span>
          <p>A sharp, strong, eye-watering smell from foods like raw garlic, onion, mustard, or strong cheese.
          It's intense and immediate — hard to ignore.
          High pungent means a very bold, sharp aroma that fills the room.</p>
        </div>
        <div className="gloss-entry">
          <span className="gloss-label">Earthy</span>
          <p>A deep, soil-like or forest-floor scent from mushrooms, truffles, beets, and root vegetables.
          Think fresh rain on dirt or a walk through the woods.
          High earthy means a rich, grounded, mushroom-like aroma.</p>
        </div>
      </div>
    </div>
  );
}

function PreferenceForm({ currentUser, onResults, onSearchSubmitted }) {
  const [mode, setMode]                       = useState('slider');
  const [preferences, setPreferences]         = useState(DEFAULT_PREFS());
  const [smellPreferences, setSmellPreferences] = useState(DEFAULT_SMELL_PREFS());
  const [exclusions, setExclusions]           = useState(DEFAULT_TASTE_EXCL());
  const [preferencePositions, setPreferencePositions] = useState({});
  const [totalItems, setTotalItems]           = useState(65);
  const [showSmell, setShowSmell]             = useState(false);
  const [smellExclusions, setSmellExclusions] = useState(DEFAULT_SMELL_EXCL());
  const [smellExcludeAll, setSmellExcludeAll] = useState(true);
  const savedSmellExclRef                     = useRef(null);

  // Seed foods
  const [seedFoods, setSeedFoods]   = useState([]);
  const [seedInput, setSeedInput]   = useState('');
  const [allFoods, setAllFoods]     = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const seedInputRef                  = useRef(null);

  const [loading, setLoading] = useState(false);

  // Switch to Instructions when user first logs in
  const prevUserRef = useRef(null);
  useEffect(() => {
    if (currentUser && !prevUserRef.current) {
      setMode('instructions');
    }
    prevUserRef.current = currentUser;
  }, [currentUser]);

  useEffect(() => {
    initializePreferences();
    loadAllFoods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePreferences = async () => {
    try {
      const data  = await fetchTotalItems();
      const items = data.total_items || 65;
      setTotalItems(items);
      const half = Math.floor(items / 2);
      const pos  = {};
      ATTRIBUTES.forEach(attr => { pos[attr] = half; });
      setPreferencePositions(pos);
    } catch (e) { console.error('Error fetching total items:', e); }
  };

  const loadAllFoods = async () => {
    try {
      const foods = await getAllFoods();
      setAllFoods(foods || []);
    } catch (e) { console.error('Could not load food list:', e); }
  };

  // ─── Taste slider (drag = integer) ───────────────────────────────────────
  const handleSliderChange = (attribute, value) => {
    const intValue   = Math.round(parseFloat(value));
    setPreferences(prev => ({ ...prev, [attribute]: intValue }));
    const newPosition = computePositionFromValue(intValue, totalItems);
    setPreferencePositions(prev => ({ ...prev, [attribute]: newPosition }));
  };

  // ─── Taste arrows (±0.1) ─────────────────────────────────────────────────
  const adjustPreference = (attribute, direction) => {
    setPreferences(prev => {
      const current = parseFloat(prev[attribute]) || 5;
      const updated = parseFloat(Math.max(1, Math.min(10,
        direction === 'higher' ? current + 0.1 : current - 0.1
      )).toFixed(1));
      const newPosition = computePositionFromValue(updated, totalItems);
      setPreferencePositions(pp => ({ ...pp, [attribute]: newPosition }));
      return { ...prev, [attribute]: updated };
    });
  };

  // ─── Smell slider (drag = integer) ───────────────────────────────────────
  const handleSmellSliderChange = (attribute, value) => {
    const intValue = Math.round(parseFloat(value));
    setSmellPreferences(prev => ({ ...prev, [attribute]: intValue }));
  };

  // ─── Smell arrows (±0.1) ─────────────────────────────────────────────────
  const adjustSmellPreference = (attribute, direction) => {
    setSmellPreferences(prev => {
      const current = parseFloat(prev[attribute]) || 5;
      const updated = parseFloat(Math.max(1, Math.min(10,
        direction === 'higher' ? current + 0.1 : current - 0.1
      )).toFixed(1));
      return { ...prev, [attribute]: updated };
    });
  };

  // ─── Taste exclusions ────────────────────────────────────────────────────
  const handleExclusionChange = (attribute) => {
    setExclusions(prev => ({ ...prev, [exclusionKeyFor(attribute)]: !prev[exclusionKeyFor(attribute)] }));
  };

  // ─── Smell exclusions ────────────────────────────────────────────────────
  const handleSmellExcludeAllToggle = () => {
    if (smellExcludeAll) {
      const restored = savedSmellExclRef.current
        || Object.fromEntries(SMELL_ATTRIBUTES.map(a => [exclusionKeyFor(a), false]));
      setSmellExclusions(restored);
      savedSmellExclRef.current = null;
      setSmellExcludeAll(false);
    } else {
      savedSmellExclRef.current = { ...smellExclusions };
      setSmellExclusions(Object.fromEntries(SMELL_ATTRIBUTES.map(a => [exclusionKeyFor(a), true])));
      setSmellExcludeAll(true);
    }
  };

  const handleSmellExclusionChange = (attr) => {
    setSmellExclusions(prev => {
      const updated    = { ...prev, [exclusionKeyFor(attr)]: !prev[exclusionKeyFor(attr)] };
      const allExcluded = SMELL_ATTRIBUTES.every(a => updated[exclusionKeyFor(a)]);
      setSmellExcludeAll(allExcluded);
      return updated;
    });
  };

  // ─── Seed food autocomplete ───────────────────────────────────────────────
  const handleSeedInputChange = (e) => {
    const val = e.target.value;
    setSeedInput(val);
    if (val.trim().length < 1) { setSuggestions([]); return; }
    const lower = val.toLowerCase();
    setSuggestions(allFoods.filter(f => f.toLowerCase().includes(lower) && !seedFoods.includes(f)).slice(0, 8));
  };
  const addSeedFood = (food) => {
    if (seedFoods.length >= 5) return;
    setSeedFoods(prev => [...prev, food]);
    setSeedInput('');
    setSuggestions([]);
    seedInputRef.current?.focus();
  };
  const removeSeedFood = (food) => setSeedFoods(prev => prev.filter(f => f !== food));

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setLoading(true);
    try {
      const username = currentUser?.username || '';
      const sliders  = {};
      ATTRIBUTES.forEach(attr => { sliders[attr] = preferences[attr]; });
      if (showSmell) {
        SMELL_ATTRIBUTES.forEach(attr => { sliders[attr] = smellPreferences[attr]; });
      }
      const allExclusions = { ...exclusions, ...smellExclusions };
      const payload = {
        username,
        sliders:    mode === 'slider' ? sliders : {},
        seedFoods:  mode === 'seed'   ? seedFoods : [],
        context:    { hot_cold: preferences.hot_cold },
        exclusions: allExclusions,
      };
      const data    = await recommendHybrid(payload);
      const results = data.results || [];
      if (onResults)        onResults(results, data.mode);
      if (onSearchSubmitted) onSearchSubmitted(preferences, preferencePositions, allExclusions, results);
    } catch (error) {
      console.error('Error submitting preferences:', error);
      alert('Error getting recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const sliderBg = (value) =>
    `linear-gradient(to right, #e8734a 0%, #e8734a ${((value - 1) / 9) * 100}%, #d3d3d3 ${((value - 1) / 9) * 100}%, #d3d3d3 100%)`;

  return (
    <div className="preference-form-container">
      <h1>JustTaste — Find Your Next Favourite Food</h1>

      {/* ── Mode Tabs ─────────────────────────────────────────────────────── */}
      <div className="mode-tabs">
        <button className={`mode-tab ${mode === 'slider' ? 'active' : ''}`} onClick={() => setMode('slider')}>
          🎚 Build by Taste
        </button>
        <button className={`mode-tab ${mode === 'seed' ? 'active' : ''}`} onClick={() => setMode('seed')}>
          🍽 Recommend from Foods I Like
        </button>
        <button className={`mode-tab ${mode === 'instructions' ? 'active' : ''}`} onClick={() => setMode('instructions')}>
          📖 Instructions
        </button>
      </div>

      {/* ── Instructions Tab ──────────────────────────────────────────────── */}
      {mode === 'instructions' && <InstructionsPage />}

      {/* ── Slider Mode ───────────────────────────────────────────────────── */}
      {mode === 'slider' && (
        <form className="preference-form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          {ATTRIBUTES.map(attr => {
            const isExcluded = exclusions[exclusionKeyFor(attr)] || false;
            return (
            <div key={attr} className={`preference-row${isExcluded ? ' row-excluded' : ''}`}>
              <label htmlFor={attr}>{attr}:</label>
              <div className="slider-container">
                <input
                  type="range" id={attr} min="1" max="10" step="0.1"
                  value={preferences[attr]}
                  onChange={(e) => handleSliderChange(attr, e.target.value)}
                  className={`preference-slider${isExcluded ? ' slider-excluded' : ''}`}
                  style={{ background: sliderBg(preferences[attr]) }}
                />
                <span className="slider-value">{displayPref(preferences[attr])}</span>
              </div>
              <span className="food-example">— {tasteExample(attr, preferences[attr])}</span>
              <div className="adjust-buttons">
                <button type="button" onClick={() => adjustPreference(attr, 'higher')}>▲</button>
                <button type="button" onClick={() => adjustPreference(attr, 'lower')}>▼</button>
              </div>
              <label className="exclude-checkbox">
                <input type="checkbox" checked={isExcluded} onChange={() => handleExclusionChange(attr)} />
                Exclude
              </label>
            </div>
            );
          })}

          {/* ── Aroma Section ────────────────────────────────────────────── */}
          <div className="smell-section">
            <button type="button" className="smell-toggle" onClick={() => setShowSmell(v => !v)}>
              {showSmell ? '▲ Hide' : '▼ Add'} Aroma Preferences (optional)
            </button>
            {showSmell && (
              <div className="smell-sliders">
                <p className="smell-hint">
                  Smell accounts for up to 80% of perceived flavour. Leave excluded to ignore.
                </p>
                <div className="smell-exclude-all-row">
                  <label className="smell-exclude-all-label">
                    <input type="checkbox" checked={smellExcludeAll} onChange={handleSmellExcludeAllToggle} />
                    <span>Exclude All Aromas</span>
                  </label>
                  <span className="smell-exclude-all-hint">
                    {smellExcludeAll ? 'Aromas excluded — click to include' : 'Click to exclude all aromas'}
                  </span>
                </div>

                {SMELL_ATTRIBUTES.map(attr => {
                  const isExcl = smellExclusions[exclusionKeyFor(attr)] || false;
                  return (
                  <div key={attr} className={`preference-row smell-row${isExcl ? ' row-excluded' : ''}`}>
                    <label htmlFor={`smell-${attr}`}>{attr} (1–10):</label>
                    <div className="slider-container">
                      <input
                        type="range" id={`smell-${attr}`} min="1" max="10" step="0.1"
                        value={smellPreferences[attr]}
                        onChange={(e) => handleSmellSliderChange(attr, e.target.value)}
                        className={`preference-slider smell-slider${isExcl ? ' slider-excluded' : ''}`}
                        style={{ background: sliderBg(smellPreferences[attr]) }}
                      />
                      <span className="slider-value">{displayPref(smellPreferences[attr])}</span>
                    </div>
                    <span className="food-example">— {smellExample(attr, smellPreferences[attr])}</span>
                    <div className="adjust-buttons">
                      <button type="button" onClick={() => adjustSmellPreference(attr, 'higher')}>▲</button>
                      <button type="button" onClick={() => adjustSmellPreference(attr, 'lower')}>▼</button>
                    </div>
                    <label className="exclude-checkbox">
                      <input type="checkbox"
                        checked={isExcl}
                        onChange={() => handleSmellExclusionChange(attr)}
                      />
                      Exclude
                    </label>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Temperature — below aromas, above Find Food */}
          <div className="hot-cold-group">
            <label>Temperature Preference:</label>
            <div className="radio-group">
              {['Hot', 'Cold', 'Both'].map(opt => (
                <label key={opt}>
                  <input type="radio" name="hot_cold" value={opt}
                    checked={preferences.hot_cold === opt}
                    onChange={(e) => setPreferences(prev => ({ ...prev, hot_cold: e.target.value }))}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Finding...' : 'Find My Food'}
          </button>
        </form>
      )}

      {/* ── Seed Mode ─────────────────────────────────────────────────────── */}
      {mode === 'seed' && (
        <div className="seed-form">
          <p className="seed-hint">
            Enter foods you already enjoy (up to 5). The algorithm will find your shared taste profile and recommend something new.
          </p>
          <div className="seed-chips">
            {seedFoods.map(food => (
              <span key={food} className="seed-chip">
                {food}
                <button type="button" className="seed-chip-remove" onClick={() => removeSeedFood(food)}>×</button>
              </span>
            ))}
            {seedFoods.length === 0 && <span className="seed-placeholder">No foods added yet…</span>}
          </div>
          {seedFoods.length < 5 && (
            <div className="seed-input-wrapper">
              <input
                ref={seedInputRef}
                type="text" className="seed-input" placeholder="Type a food name…"
                value={seedInput} onChange={handleSeedInputChange}
                onKeyDown={(e) => { if (e.key === 'Enter' && suggestions.length > 0) { e.preventDefault(); addSeedFood(suggestions[0]); } }}
              />
              {suggestions.length > 0 && (
                <ul className="seed-suggestions">
                  {suggestions.map(s => <li key={s} onClick={() => addSeedFood(s)}>{s}</li>)}
                </ul>
              )}
            </div>
          )}
          <div className="hot-cold-group">
            <label>Temperature Preference:</label>
            <div className="radio-group">
              {['Hot', 'Cold', 'Both'].map(opt => (
                <label key={opt}>
                  <input type="radio" name="hot_cold_seed" value={opt}
                    checked={preferences.hot_cold === opt}
                    onChange={(e) => setPreferences(prev => ({ ...prev, hot_cold: e.target.value }))}
                  />
                  {opt}
                </label>
              ))}
            </div>
          </div>
          <button type="button" className="submit-button"
            disabled={loading || seedFoods.length === 0} onClick={handleSubmit}>
            {loading ? 'Finding...' : `Recommend from ${seedFoods.length} food${seedFoods.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      )}
    </div>
  );
}

export default PreferenceForm;
